// src/components/Sidebar.jsx
import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useBranding } from "../contexts/BrandingContext";
import useIsHeld from "../hooks/useIsHeld";
import {
  LayoutDashboard,
  ClipboardList,
  Flag,
  Clock,
  FileSignature,
  FileCheck,
  CalendarDays,
  RefreshCw,
  Plane,
  Megaphone,
  // Notices. Megaphone is already "Post Availability" in this same list.
  BellRing,
  LayoutGrid,
  Inbox,
  Send,
  Store,
  BadgeCheck,
  Calculator,
  Receipt,
  CreditCard,
  Users,
  ChevronDown,
  UserRound,
  LifeBuoy,
  MessageSquare,
} from "lucide-react";

const COLLAPSE_KEY = "sift_sidebar_collapsed_v1";
// Section labels in order. On first visit we collapse everything except the
// first group (Work/Dashboard) so the sidebar opens tidy, not fully expanded.
const GROUP_LABELS = ["Work", "Schedule", "Marketplace", "Credentials & Money"];

export default function Sidebar({ onClose }) {
  const branding = useBranding();
  const isCreamLabel = branding.branded;
  const held = useIsHeld();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch (_) {}
    // First visit: collapse all but the first group.
    return new Set(GROUP_LABELS.slice(1));
  });
  const toggleGroup = (label) => setCollapsed((prev) => {
    const next = new Set(prev);
    next.has(label) ? next.delete(label) : next.add(label);
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next])); } catch (_) {}
    return next;
  });

  // Grouped navigation. Every path / dataTour / tag / hideOnCreamLabel
  // is preserved exactly as before — only the visual grouping is new.
  const navGroups = [
    {
      label: "Work",
      items: [
        { label: "Dashboard", path: "/dashboard/contractor", icon: LayoutDashboard },
        { label: "My assignments", path: "/dashboard/contractor/assignments", dataTour: "contractor-assignments", icon: ClipboardList },
        { label: "Milestones", path: "/dashboard/contractor/milestones", icon: Flag },
        { label: "Timecard", path: "/dashboard/contractor/timecard", dataTour: "contractor-timecard", icon: Clock },
        { label: "Contract Center", path: "/dashboard/contractor/contract-center", dataTour: "contractor-contracts", icon: FileSignature },
        { label: "Documents to sign", path: "/dashboard/contractor/documents", icon: FileCheck },
        { label: "Notices", path: "/dashboard/notices", icon: BellRing },
      ],
    },
    {
      label: "Schedule",
      items: [
        { label: "Calendar", path: "/dashboard/contractor/calendar", icon: CalendarDays },
        { label: "Calendar sync", path: "/dashboard/contractor/settings/calendar-sync", icon: RefreshCw },
        { label: "Travel Center", path: "/dashboard/contractor/travel", icon: Plane },
      ],
    },
    {
      label: "Marketplace",
      items: [
        { label: "Browse jobs", path: "/browse?show=job", icon: LayoutGrid, hideWhenHeld: true },
        { label: "Browse services", path: "/browse?show=service", icon: Users },
        { label: "Post availability", path: "/dashboard/contractor/postings", dataTour: "contractor-postings", icon: Megaphone, hideWhenHeld: true },
        { label: "Messages", path: "/messages", icon: MessageSquare },
        { label: "Inbound requests", path: "/dashboard/contractor/inbox", dataTour: "contractor-inbox", icon: Inbox },
        { label: "My requests", path: "/dashboard/my-requests", icon: Send },
        { label: "Public profile editor", path: "/dashboard/contractor/storefront", dataTour: "contractor-storefront", icon: Store },
      ],
    },
    {
      label: "Credentials & money",
      items: [
        { label: "Credentials", path: "/dashboard/contractor/credentials", dataTour: "contractor-credentials", icon: BadgeCheck },
        { label: "Accounting Center", path: "/dashboard/contractor/accounting", dataTour: "contractor-accounting", icon: Calculator },
        { label: "Tax Center", path: "/dashboard/contractor/taxes", icon: Receipt },
        { label: "Stripe setup", path: "/dashboard/contractor/stripe", dataTour: "contractor-stripe", icon: CreditCard },
        { label: "Partner program", path: "/dashboard/partner", tag: "Invite", hideOnCreamLabel: true, icon: Users },
      ],
    },
  ];

  const visibleGroups = navGroups
    .map((group) => ({
      ...group,
      items: (isCreamLabel
        ? group.items.filter((item) => !item.hideOnCreamLabel)
        : group.items
      ).filter((item) => !(held && item.hideWhenHeld)),
    }))
    .filter((group) => group.items.length > 0);

  const handleNavigate = () => {
    if (onClose) onClose();
  };

  return (
    <div
      className="w-full h-full flex flex-col p-4 rounded-2xl border border-line"
      style={{ backgroundColor: isCreamLabel ? branding.accent_color : "#FCFAF5" }}
    >
      {/* A partner's own brand heads its sidebar. Sift's wordmark is already
          in the top bar, so it isn't repeated here. */}
      {isCreamLabel && (
        <div className="flex items-center justify-between mb-2">
          {branding.brand_logo_url ? (
            <img src={branding.brand_logo_url} alt={branding.brand_name} className="h-7 max-w-[160px] object-contain" />
          ) : (
            <h2 className="text-2xl font-bold text-white">{branding.brand_name}</h2>
          )}
        </div>
      )}

      <nav aria-label="Sidebar" className="flex-1 overflow-y-auto -mx-1 px-1">
        {visibleGroups.map((group) => {
          const isCollapsed = collapsed.has(group.label);
          return (
          <div key={group.label}>
            <button
              type="button"
              onClick={() => toggleGroup(group.label)}
              aria-expanded={!isCollapsed}
              className="w-full flex items-center justify-between px-2 py-1 mt-4 first:mt-0 mb-1 hover:opacity-80 transition-opacity"
            >
              <span className="text-[0.8125rem] font-medium text-tertiary">{group.label}</span>
              <ChevronDown className={`h-4 w-4 text-tertiary transition-transform duration-200 ${isCollapsed ? "-rotate-90" : ""}`} />
            </button>
            {!isCollapsed && (
            <ul className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      data-tour={item.dataTour}
                      onClick={handleNavigate}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-[0.9375rem] transition-colors border-l-[3px] ${
                          isActive
                            ? isCreamLabel
                              ? "font-semibold text-white border-transparent"
                              : "bg-brand-tint text-brand font-semibold border-brand"
                            : "border-transparent text-secondary hover:bg-sunken hover:text-ink"
                        }`
                      }
                      style={({ isActive }) =>
                        isActive && isCreamLabel
                          ? { backgroundColor: branding.primary_color, borderColor: branding.primary_color }
                          : undefined
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {Icon ? (
                            <Icon
                              size={19}
                              className={
                                isActive
                                  ? isCreamLabel
                                    ? "text-white"
                                    : "text-brand"
                                  : "text-tertiary"
                              }
                            />
                          ) : null}
                          <span className="flex-1">{item.label}</span>
                          {item.tag ? (
                            <span className="text-[0.625rem] px-2 py-0.5 rounded-full border border-line text-tertiary">
                              {item.tag}
                            </span>
                          ) : null}
                        </>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
            )}
          </div>
          );
        })}
      </nav>

      <div className="pt-4 mt-2 border-t border-line space-y-1">
        {/* "Edit my profile" was a muted 14px text link sitting below the fold
            of the nav, styled like fine print. For a solo user it's one of the
            two things they came to do, so it now reads as a nav item. */}
        <NavLink
          to="/dashboard/contractor/profile"
          data-tour="contractor-profile"
          onClick={handleNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[0.9375rem] font-medium transition-colors border-l-[3px] ${
              isActive
                ? "bg-brand-tint text-brand border-brand"
                : "border-transparent text-ink hover:bg-sunken"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <UserRound size={19} className={isActive ? "text-brand" : "text-tertiary"} />
              <span>My profile</span>
            </>
          )}
        </NavLink>
        <NavLink
          to="/dashboard/support"
          onClick={handleNavigate}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[0.9375rem] text-secondary hover:bg-sunken hover:text-ink transition-colors border-l-[3px] border-transparent"
        >
          <LifeBuoy size={19} className="text-tertiary" />
          <span>Support</span>
        </NavLink>
        {isCreamLabel && (
          <>
            <a
              href="https://siftcareers.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block px-3 pt-2 text-sm text-tertiary hover:text-ink transition-colors"
            >
              Visit Sift Careers →
            </a>
            <div className="text-[0.625rem] text-faint pt-1">
              Powered by Sift Careers
            </div>
          </>
        )}
      </div>
    </div>
  );
}
